import { notification } from "antd";
import classNames from "classnames";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck } from "react-icons/bi";
import {
  MdOutlineBookmark,
  MdOutlineCheckCircle,
  MdOutlineMap,
  MdOutlineWarning,
} from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { ADD_REFERENTIAL } from "utils/api/graphql/mutations/geo-zones";
import { REFERENTIALS } from "utils/api/graphql/queries/geo-zones";
import { ALL_SOCIAL_REGIMES, BENEFICIARY_TYPES } from "utils/constants";
import { COLORS } from "shared/style/colors";
import { REFERENTIAL_QUERY_LIMIT, REFERENTIAL_TYPES } from "./constants";
import StepCard from "./StepCard";

const StepValueMapping = ({
  preview,
  onConfirm,
  persistedState,
  onStateChange,
  onBack,
  backLabel,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const IMPORT = t("IMPORT", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });
  const VM = IMPORT.VALUE_MAPPING;

  const fileRegimes = get(preview, "regimes", []).filter(Boolean);
  const fileBenefTypes = get(preview, "beneficiaryTypes", []).filter(Boolean);

  const {
    regimeMap,
    benefTypeMap,
    saveRegime,
    saveBenef,
    regimeRefName,
    benefRefName,
    selectedRegimeRefId,
    selectedBenefRefId,
  } = persistedState;

  const updateField = (field, value) => {
    onStateChange((prev) => ({ ...prev, [field]: value }));
  };

  const setRegimeMap = (val) =>
    onStateChange((prev) => ({
      ...prev,
      regimeMap: typeof val === "function" ? val(prev.regimeMap) : val,
    }));
  const setBenefTypeMap = (val) =>
    onStateChange((prev) => ({
      ...prev,
      benefTypeMap: typeof val === "function" ? val(prev.benefTypeMap) : val,
    }));
  const setSaveRegime = (val) => updateField("saveRegime", val);
  const setSaveBenef = (val) => updateField("saveBenef", val);
  const setRegimeRefName = (val) => updateField("regimeRefName", val);
  const setBenefRefName = (val) => updateField("benefRefName", val);
  const setSelectedRegimeRefId = (val) =>
    updateField("selectedRegimeRefId", val);
  const setSelectedBenefRefId = (val) => updateField("selectedBenefRefId", val);

  const [saving, setSaving] = useState(false);

  const { data: regimeRefsData } = useQuery(REFERENTIALS, {
    variables: {
      where: { type: REFERENTIAL_TYPES.REGIME },
      take: REFERENTIAL_QUERY_LIMIT,
    },
  });
  const { data: benefRefsData } = useQuery(REFERENTIALS, {
    variables: {
      where: { type: REFERENTIAL_TYPES.BENEFICIARY_TYPE },
      take: REFERENTIAL_QUERY_LIMIT,
    },
  });

  const existingRegimeRefs = get(regimeRefsData, "referentials.data", []);
  const existingBenefRefs = get(benefRefsData, "referentials.data", []);

  const [addReferential] = useMutation(ADD_REFERENTIAL);

  const regimeOptions = Object.values(ALL_SOCIAL_REGIMES).map((value) => ({
    label: FORM.SOCIAL_REGIMES_OPTIONS[value],
    value,
  }));

  const benefOptions = Object.values(BENEFICIARY_TYPES).map((value) => ({
    label: FORM.BENEFICIARY_OPTIONS[value],
    value,
  }));

  const regimesMapped = fileRegimes.filter((r) => regimeMap[r]).length;
  const benefTypesMapped = fileBenefTypes.filter((b) => benefTypeMap[b]).length;

  const allRegimesMapped = regimesMapped === fileRegimes.length;
  const allBenefMapped = benefTypesMapped === fileBenefTypes.length;
  const allMapped = allRegimesMapped && allBenefMapped;

  const canSave =
    (!saveRegime || regimeRefName.trim()) &&
    (!saveBenef || benefRefName.trim());

  const handleLoadRegimeRef = (refId) => {
    if (!refId) {
      setSelectedRegimeRefId(null);
      return;
    }
    const ref = existingRegimeRefs.find((r) => r.id === refId);
    if (!ref || !ref.entries) return;
    setRegimeMap((prev) => ({ ...prev, ...ref.entries }));
    setRegimeRefName(ref.name);
    setSelectedRegimeRefId(ref.id);
    setSaveRegime(true);
  };

  const handleLoadBenefRef = (refId) => {
    if (!refId) {
      setSelectedBenefRefId(null);
      return;
    }
    const ref = existingBenefRefs.find((r) => r.id === refId);
    if (!ref || !ref.entries) return;
    setBenefTypeMap((prev) => ({ ...prev, ...ref.entries }));
    setBenefRefName(ref.name);
    setSelectedBenefRefId(ref.id);
    setSaveBenef(true);
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      if (saveRegime && regimeRefName.trim()) {
        const regimeData = {
          name: regimeRefName.trim(),
          type: REFERENTIAL_TYPES.REGIME,
          entries: regimeMap,
        };
        if (selectedRegimeRefId) {
          regimeData.id = selectedRegimeRefId;
        }
        await addReferential({
          refetchQueries: [
            {
              query: REFERENTIALS,
              variables: {
                where: { type: REFERENTIAL_TYPES.REGIME },
                take: REFERENTIAL_QUERY_LIMIT,
              },
            },
          ],
          variables: { data: regimeData },
        });
      }
      if (saveBenef && benefRefName.trim()) {
        const benefData = {
          name: benefRefName.trim(),
          type: REFERENTIAL_TYPES.BENEFICIARY_TYPE,
          entries: benefTypeMap,
        };
        if (selectedBenefRefId) {
          benefData.id = selectedBenefRefId;
        }
        await addReferential({
          refetchQueries: [
            {
              query: REFERENTIALS,
              variables: {
                where: { type: REFERENTIAL_TYPES.BENEFICIARY_TYPE },
                take: REFERENTIAL_QUERY_LIMIT,
              },
            },
          ],
          variables: { data: benefData },
        });
      }
    } catch (error) {
      notification.error({
        message: VM.REFERENTIAL_SAVE_ERROR,
        description: error.message,
      });
    } finally {
      setSaving(false);
    }

    onConfirm(regimeMap, benefTypeMap);
  };

  const renderMappingStatus = (mapped, total) => {
    const isDone = mapped === total;
    return (
      <div
        className={classNames("pricers-import__mapping-status", {
          "pricers-import__mapping-status--done": isDone,
          "pricers-import__mapping-status--pending": !isDone,
        })}
      >
        {isDone ? (
          <MdOutlineCheckCircle size={16} />
        ) : (
          <MdOutlineWarning size={16} />
        )}
        {isDone ? VM.ALL_MAPPED : `${total - mapped} ${VM.REMAINING}`}
      </div>
    );
  };

  const renderMappingSection = (
    title,
    fileValues,
    valueMap,
    setValueMap,
    options,
    existingRefs,
    onLoadRef,
    shouldSave,
    setShouldSave,
    refName,
    setRefName,
  ) => {
    if (fileValues.length === 0) return null;
    const mapped = fileValues.filter((v) => !!valueMap[v]).length;

    return (
      <div className="pricers-import__vm-section">
        <div className="pricers-import__vm-section-header">
          <h4 className="m-0">{title}</h4>
          {renderMappingStatus(mapped, fileValues.length)}
        </div>

        {existingRefs.length > 0 && (
          <div className="pricers-import__ref-loader">
            <MdOutlineMap size={20} color={COLORS.C_PRIMARY} />
            <span className="pricers-import__ref-loader-label">
              {VM.LOAD_EXISTING}
            </span>
            <Select
              options={existingRefs.map((r) => ({
                label: r.name,
                value: r.id,
              }))}
              placeholder={VM.SELECT_REFERENTIAL}
              onChange={onLoadRef}
              allowClear
              className="w-250"
            />
          </div>
        )}

        <div className="pricers-import__vm-rows">
          {fileValues.map((fileVal) => {
            const isMapped = !!valueMap[fileVal];
            return (
              <div
                key={fileVal}
                className={classNames("pricers-import__vm-row", {
                  "pricers-import__vm-row--mapped": isMapped,
                })}
              >
                <div className="pricers-import__vm-file-value">
                  <span
                    className={classNames("pricers-import__status-dot", {
                      "pricers-import__status-dot--mapped": isMapped,
                      "pricers-import__status-dot--required": !isMapped,
                    })}
                  />
                  <span className="pricers-import__file-value">{fileVal}</span>
                </div>
                <div className="pricers-import__vm-arrow">→</div>
                <Select
                  options={options}
                  value={valueMap[fileVal] || undefined}
                  placeholder={VM.SELECT_PLACEHOLDER}
                  onChange={(val) =>
                    setValueMap((prev) => ({ ...prev, [fileVal]: val }))
                  }
                  allowClear
                  className="pricers-import__vm-select"
                />
              </div>
            );
          })}
        </div>

        <div className="pricers-import__save-section">
          <div className="d-flex align--center gap-10">
            <input
              type="checkbox"
              checked={shouldSave}
              onChange={(e) => setShouldSave(e.target.checked)}
              className="pricers-checkbox"
            />
            <MdOutlineBookmark size={16} color={COLORS.C_PRIMARY} />
            <span className="pricers-import__save-label">
              {VM.SAVE_AS_REFERENTIAL}
            </span>
          </div>
          {shouldSave && (
            <Input
              placeholder={VM.REFERENTIAL_NAME_PLACEHOLDER}
              value={refName}
              onChange={(e) => setRefName(e.target.value)}
              className="mt-10"
            />
          )}
        </div>
      </div>
    );
  };

  const confirmButton = (
    <Button
      type="primary"
      onClick={handleConfirm}
      disabled={!allMapped || !canSave || saving}
      loading={saving}
    >
      <BiCheck size={20} />
      {VM.CONFIRM}
    </Button>
  );

  return (
    <StepCard
      title={VM.TITLE}
      description={VM.DESCRIPTION}
      onBack={onBack}
      backLabel={backLabel}
      actions={confirmButton}
    >
      {renderMappingSection(
        VM.REGIMES_TITLE,
        fileRegimes,
        regimeMap,
        setRegimeMap,
        regimeOptions,
        existingRegimeRefs,
        handleLoadRegimeRef,
        saveRegime,
        setSaveRegime,
        regimeRefName,
        setRegimeRefName,
      )}

      {renderMappingSection(
        VM.BENEFICIARY_TITLE,
        fileBenefTypes,
        benefTypeMap,
        setBenefTypeMap,
        benefOptions,
        existingBenefRefs,
        handleLoadBenefRef,
        saveBenef,
        setSaveBenef,
        benefRefName,
        setBenefRefName,
      )}
    </StepCard>
  );
};

export default StepValueMapping;
